import type { CustomCssVariables } from '@thoughtspot/visual-embed-sdk';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import { getInfoObject } from 'utils/tokenService';

type TsCssVariable = keyof CustomCssVariables;

export interface CustomFont {
  name: string;
  link: string;
  weight: string;
  style: string;
  value: string;
  label: string;
}

const initialStyleState: Partial<Record<TsCssVariable, string>> = {};

export const StyleContext = createContext({
  styleState: initialStyleState,
  resetStyleState: (state: Partial<Record<TsCssVariable, string>>) => {
    state;
  },
  customFonts: [] as CustomFont[],
  styleStateKey: '',
  updateVariable: (key: TsCssVariable, value: string) => {
    key;
    value;
  },
  updateVariableInBulk: (variables: CustomCssVariables) => {
    variables;
  },
  addCustomFont: (font: CustomFont) => {
    font;
  },
  removeCustomFont: (fontValue: string) => {
    fontValue;
  },
  getCustomFontRules: () => ({}),
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  resetToDefault: () => {},
});

export const StyleContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [customFonts, setCustomFonts] = useState<CustomFont[]>([]);
  const [styleState, setStyleState] = useState({
    data: {},
    key: '',
  });

  useEffect(() => {
    const fetchCurrentStyleStateKey = async () => {
      const infoObject = await getInfoObject();
      const styleStateKey =
        'styleState' +
        (infoObject?.info
          ? `-${infoObject?.info?.userName}-${infoObject?.info?.currentOrgName}`
          : '');
      try {
        const styleStateLocal = JSON.parse(
          localStorage.getItem(styleStateKey) || localStorage.getItem('styleState') || '{}'
        );
        localStorage.removeItem('styleState');

        setStyleState((prev) => ({
          ...prev,
          key: styleStateKey,
          data: styleStateLocal,
        }));
      } catch (error) {
        setStyleState((prev) => ({
          ...prev,
          key: styleStateKey,
        }));
      }
    };
    fetchCurrentStyleStateKey();
  }, []);

  const updateVariable = (key: TsCssVariable, value: string) => {
    const currentState = { ...styleState.data };
    if (value === undefined) {
      delete currentState[key];
    } else {
      currentState[key] = value;
    }
    setStyleState((prev) => ({
      ...prev,
      data: currentState,
    }));
  };

  // Update multiple variables at once
  // could have used name updateVariable but its too smilar to the above function causing confusion
  const updateVariableInBulk = useCallback((variables: CustomCssVariables) => {
    setStyleState((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        ...variables,
      },
    }));
  }, []);

  const addCustomFont = (font: CustomFont) => {
    setCustomFonts((prev) => [...prev, font]);
  };

  const removeCustomFont = (fontValue: string) => {
    setCustomFonts((prev) => prev.filter((font) => font.value !== fontValue));
  };

  const getCustomFontRules = () => {
    const rules: Record<string, any> = {};

    customFonts.forEach((font, index) => {
      const ruleKey = `/* custom-${font.name.toLowerCase().replace(/\s+/g, '-')}-${
        font.weight
      } */ @font-face`;

      let fontSrc = `url(${font.link})`;
      if (font.link.includes('.woff2')) fontSrc = `url(${font.link}) format('woff2')`;

      rules[ruleKey] = {
        'font-family': font.name,
        'font-style': `"${font.style}"`,
        'font-weight': `"${font.weight}"`,
        'font-display': 'swap',
        src: fontSrc,
      };
    });

    return rules;
  };

  const resetToDefault = async () => {
    setStyleState((prev) => ({
      ...prev,
      data: initialStyleState,
    }));
    localStorage.setItem(styleState.key, '{}');
    setCustomFonts([]);
  };

  const resetStyleState = (state: Partial<Record<TsCssVariable, string>>) => {
    setStyleState((prev) => ({
      ...prev,
      data: state,
    }));
  };

  return (
    <StyleContext.Provider
      value={{
        styleState: styleState.data,
        resetStyleState,
        customFonts,
        styleStateKey: styleState.key,
        updateVariable,
        updateVariableInBulk,
        addCustomFont,
        removeCustomFont,
        getCustomFontRules,
        resetToDefault,
      }}
    >
      {children}
    </StyleContext.Provider>
  );
};

export const useStyleContext = () => {
  return useContext(StyleContext);
};
